import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as yup from 'yup';
import { Button, Width, Modal, SubmitStates } from '@figure/frontlib-gui';
import { useIncentive, PRODUCTS, INCENTIVE_STATUS } from '@figure/frontlib-growth';
import { useProfile, useTelemetry, usePersonalLoan, useRedirect } from 'shared/hooks';
import { PageHeader } from 'shared/components/Typography';
import NPSCollection from './NPSCollection';
import ShirtForm from './ShirtForm';
import ShareReferralModal from './ShareReferralModal';

const ButtonContainer = styled.div`
  text-align: center;
  margin-top: 30px;

  // hack since Button isn't a styled component
  > button {
    margin-top: 15px;

    @media print, screen and (min-width: ${Width.SM}px) {
      margin: 0 0 0 15px;
    }
  }
`;

const CompleteContainer = () => {
  const { profile } = useProfile();
  const { incentiveReferral, status } = useIncentive(PRODUCTS.PERSONAL_LOAN);
  const { telemetry } = useTelemetry();
  const [referralModalOpen, setReferralModalOpen] = useState(true);
  const [showReferralConfirm, setShowReferralConfirm] = useState(false);
  const [submitState, setSubmitState] = useState(SubmitStates.DEFAULT);
  const [serverErrors, setServerErrors] = useState({});
  const { personalLoan, updatePersonalLoan, error: serverError } = usePersonalLoan();
  const { redirectToDashboard } = useRedirect();
  const [isOpen, setIsOpen] = useState(false);
  const [t] = useTranslation('');
  const companySingleName = t('companySingleName');

  useEffect(() => telemetry('plCompleted'), [telemetry]);
  useEffect(() => {
    if (status === INCENTIVE_STATUS.OPEN) {
      setShowReferralConfirm(true);
    }
  }, [setShowReferralConfirm, status]);

  const handleDashboardClick = () => redirectToDashboard();
  const onSubmit = (data) => {
    setSubmitState(SubmitStates.PENDING);
    incentiveReferral(process.env.REACT_APP_PL_REFERRAL_INCENTIVE_ID, data.email)
      .then((res) => {
        if (res.status === INCENTIVE_STATUS.PREOPEN) {
          setSubmitState(SubmitStates.SUCCESS);
        } else {
          setSubmitState(SubmitStates.DEFAULT);
          setServerErrors({ email: 'Something unexpected happened. Please try again.' });
        }
      })
      .catch(() => {
        setSubmitState(SubmitStates.DEFAULT);
        setServerErrors({ email: 'Something unexpected happened. Please try again.' });
      });
  };
  return (
    <>
      <PageHeader>
        Your funds are on the way, <span className="fs-mask">{profile.name?.firstName ?? ''}</span>!
      </PageHeader>
      <p>
        Thank you for choosing {companySingleName}. We’re here for you so please call or email us if
        we can be of service.
      </p>

      <Formik
        initialValues={{ npsScore: '' }}
        validationSchema={yup.object().shape({
          npsScore: yup
            .string()
            .required('A survey selection is required to claim your free gift.'),
        })}
        onSubmit={(data) => {
          updatePersonalLoan(personalLoan.uuid, { ...personalLoan, npsScore: data.npsScore });
          setIsOpen(true);
        }}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit }) => (
          <>
            {!personalLoan.hasTShirtSize && (
              <>
                <hr aria-hidden="true" />
                <NPSCollection
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </>
            )}
            <ButtonContainer>
              <Button large onClick={handleDashboardClick} primary={personalLoan.hasTShirtSize}>
                Go to dashboard
              </Button>

              {!personalLoan.hasTShirtSize && (
                <Button large primary onClick={handleSubmit}>
                  Submit and claim gift
                </Button>
              )}
            </ButtonContainer>
          </>
        )}
      </Formik>

      <Modal
        header="Receive a free Figure t-shirt"
        size="sm"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        closable
      >
        <ShirtForm
          handleSubmit={async (data) => {
            await updatePersonalLoan(personalLoan.uuid, {
              ...personalLoan,
              tShirtSize: data.tShirtSize,
            });

            setIsOpen(false);
          }}
          serverError={serverError}
        />
      </Modal>
      <ShareReferralModal
        isOpen={referralModalOpen}
        handleClose={() => setReferralModalOpen(false)}
        handleSubmit={onSubmit}
        submitState={submitState}
        setSubmitState={setSubmitState}
        showReferralConfirm={showReferralConfirm}
        setShowReferralConfirm={setShowReferralConfirm}
        serverErrors={serverErrors}
        refereeEmail={profile.email}
      />
    </>
  );
};

export default CompleteContainer;
